import { Provider } from '@angular/core';
import { injectDispatch } from '@ngrx/signals/events';
import { TransactionEvents } from '@app/domain/events';
import {
  GetCreditorsUseCase,
  GetCreditorByIdUseCase,
  CountCreditorsUseCase,
  CreateCreditorUseCase,
  UpdateCreditorUseCase,
  DeleteCreditorUseCase,
  SetDebtClosedUseCase,
  GetCreditorPaymentsUseCase,
  CreateCreditorPaymentUseCase,
  UpdateCreditorPaymentUseCase,
  DeleteCreditorPaymentUseCase,
} from '@app/domain/use-cases';
import type { ICreditorRepository, ICreditorPaymentRepository } from '@app/domain/ports';
import { CREDITOR_REPOSITORY, CREDITOR_PAYMENT_REPOSITORY } from '../../tokens/infrastructure.tokens';

/**
 * Provides Creditor domain use cases.
 *
 * Includes:
 * - Creditor Use Cases: GetCreditors, GetCreditorById, Count, Create, Update, Delete
 * - Payment Use Cases: GetCreditorPayments, CreateCreditorPayment, DeleteCreditorPayment
 *
 * All providers use constructor injection pattern following Clean Architecture.
 */
export function provideCreditorDomain(): Provider[] {
  return [
    // Creditor Use Cases - Query
    {
      provide: GetCreditorsUseCase,
      useFactory: (repository: ICreditorRepository) => new GetCreditorsUseCase(repository),
      deps: [CREDITOR_REPOSITORY],
    },
    {
      provide: GetCreditorByIdUseCase,
      useFactory: (repository: ICreditorRepository) => new GetCreditorByIdUseCase(repository),
      deps: [CREDITOR_REPOSITORY],
    },
    {
      provide: CountCreditorsUseCase,
      useFactory: (repository: ICreditorRepository) => new CountCreditorsUseCase(repository),
      deps: [CREDITOR_REPOSITORY],
    },

    // Creditor Use Cases - Commands
    {
      provide: CreateCreditorUseCase,
      useFactory: (repository: ICreditorRepository) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new CreateCreditorUseCase(repository, dispatch);
      },
      deps: [CREDITOR_REPOSITORY],
    },
    {
      provide: UpdateCreditorUseCase,
      useFactory: (repository: ICreditorRepository) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new UpdateCreditorUseCase(repository, dispatch);
      },
      deps: [CREDITOR_REPOSITORY],
    },
    {
      provide: DeleteCreditorUseCase,
      useFactory: (repository: ICreditorRepository) => {
        const dispatch = injectDispatch(TransactionEvents);
        return new DeleteCreditorUseCase(repository, dispatch);
      },
      deps: [CREDITOR_REPOSITORY],
    },
    {
      provide: SetDebtClosedUseCase,
      useFactory: (repository: ICreditorRepository) => new SetDebtClosedUseCase(repository),
      deps: [CREDITOR_REPOSITORY],
    },

    // Payment Use Cases - Query
    {
      provide: GetCreditorPaymentsUseCase,
      useFactory: (repository: ICreditorPaymentRepository) => new GetCreditorPaymentsUseCase(repository),
      deps: [CREDITOR_PAYMENT_REPOSITORY],
    },

    // Payment Use Cases - Commands
    {
      provide: CreateCreditorPaymentUseCase,
      useFactory: (paymentRepository: ICreditorPaymentRepository, creditorRepository: ICreditorRepository) =>
        new CreateCreditorPaymentUseCase(paymentRepository, creditorRepository),
      deps: [CREDITOR_PAYMENT_REPOSITORY, CREDITOR_REPOSITORY],
    },
    {
      provide: UpdateCreditorPaymentUseCase,
      useFactory: (paymentRepository: ICreditorPaymentRepository, creditorRepository: ICreditorRepository) =>
        new UpdateCreditorPaymentUseCase(paymentRepository, creditorRepository),
      deps: [CREDITOR_PAYMENT_REPOSITORY, CREDITOR_REPOSITORY],
    },
    {
      provide: DeleteCreditorPaymentUseCase,
      useFactory: (paymentRepository: ICreditorPaymentRepository, creditorRepository: ICreditorRepository) =>
        new DeleteCreditorPaymentUseCase(paymentRepository, creditorRepository),
      deps: [CREDITOR_PAYMENT_REPOSITORY, CREDITOR_REPOSITORY],
    },
  ];
}
